import * as i0 from '@angular/core';
import { Injectable, Directive, Input, EventEmitter, Output, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';

class DragAndDropService {
    constructor() {
        this._data = null;
        this.eventSource = new Subject();
        this.eventSource = new Subject();
        this.event$ = this.eventSource.asObservable();
    }
    get type() {
        return this._type;
    }
    get data() {
        return this._data;
    }
    start(type, data) {
        this._type = type;
        this._data = data;
        this.eventSource.next({
            name: 'start',
            type: type,
            data: data
        });
    }
    stop() {
        this._type = '';
        this._data = null;
        this.eventSource.next({
            name: 'stop'
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: DragAndDropService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: DragAndDropService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: DragAndDropService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

const lightThemeImg = new Image();
lightThemeImg.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAA0ElEQVRYR+3XPWpCQRSG4ediYydBsDAbEBKwEFyB2m' +
    'QBWY5gynQpsoCQLdhoZeVKhEA6S6swxQUZuBFkuLc5087fO++B8zGVjkfV8f0CIAyEgTAQBsJASQNv+GkItwec8ZHPlwJ4xhCHBoAxXvGL7+s1pQCe8IhdA0A' +
    'C7OOEJb7qdW0BTLDAETMM8J4g2gJIr5+ihwvm+GwTIK/Mqi5XWwZygBdsuzQQAGEgDBQ3cCuM8j6Q8mBfsg+kszb/xHEegCOsSwPc9c8t1Yrvujxt+gNH+CUh' +
    'rH796gAAAABJRU5ErkJggg==';
const darkThemeImg = new Image();
darkThemeImg.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAAAN1JREFUWEftly0KQkEURs+HxSYiGHQDgoJBc' +
    'AVqcQEuR9BoM7gAcQsWTSZXIgg2o+XKhAfyeO8FGeaVO3X+zpwL92NEzUM1348DuAE34AbcgBuIZsDMtsCzJNzawFvSPj8fBcDMRkBH0q0IwMx6wAp4STr9rokFMAT6ki4lAAGwCTyAuaRj' +
    'ti4VwACYAXdgArQk7QJEKoDw+jHQAD7AVNIhGUC+LGa2yMqVxEABwFLSuU4DDuAG3EB0A5VhVNAHQh5co/WBcJCZbSriOB+AXUnrqAD/fnKjtOJ/Lw/7vrtsfyGZ3LaeAAAAAElFTkSuQmCC';
class DraggableDirective {
    set rcpDraggable(draggable) {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'draggable', draggable ? 'true' : 'false');
    }
    constructor(elementRef, renderer, zone, dragHelper) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.zone = zone;
        this.dragHelper = dragHelper;
        this.rcpDragEffect = 'all';
        this.rcpDragTheme = 'light';
    }
    ngOnInit() {
        this.attachListeners();
    }
    ngOnDestroy() {
        this.detachListeners();
    }
    attachListeners() {
        const element = this.elementRef.nativeElement;
        this.zone.runOutsideAngular(() => {
            this.dragStartListener = this.renderer.listen(element, 'dragstart', this.onDragStart.bind(this));
            this.dragEndListener = this.renderer.listen(element, 'dragend', this.onDragEnd.bind(this));
        });
    }
    detachListeners() {
        this.zone.runOutsideAngular(() => {
            this.dragStartListener();
            this.dragEndListener();
        });
    }
    onDragStart(event) {
        const dragType = this.rcpDragType;
        const dragData = this.rcpDragData;
        const dragEffect = this.rcpDragEffect;
        try {
            event.dataTransfer.setData('text/plain', dragType);
        }
        catch (ex) {
            // MSIE 11 requires format to be 'Text'
            event.dataTransfer.setData('Text', dragType);
        }
        if ('setDragImage' in event.dataTransfer) {
            const imgToSet = this.rcpDragTheme === 'dark' ? darkThemeImg : lightThemeImg;
            event.dataTransfer.setDragImage(imgToSet, 0, 0);
        }
        event.dataTransfer.effectAllowed = dragEffect;
        this.zone.run(() => {
            this.dragHelper.start(dragType, dragData);
        });
        event.stopPropagation();
    }
    onDragEnd(event) {
        this.zone.run(() => {
            this.dragHelper.stop();
        });
        event.stopPropagation();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: DraggableDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.NgZone }, { token: DragAndDropService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: DraggableDirective, selector: "[rcpDraggable]", inputs: { rcpDraggable: "rcpDraggable", rcpDragData: "rcpDragData", rcpDragType: "rcpDragType", rcpDragEffect: "rcpDragEffect", rcpDragTheme: "rcpDragTheme" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: DraggableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rcpDraggable]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.NgZone }, { type: DragAndDropService }], propDecorators: { rcpDraggable: [{
                type: Input
            }], rcpDragData: [{
                type: Input
            }], rcpDragType: [{
                type: Input
            }], rcpDragEffect: [{
                type: Input
            }], rcpDragTheme: [{
                type: Input
            }] } });

class DroppableDirective {
    set disabled(disabled) {
        if (disabled) {
            this.detachListeners();
        }
        else {
            this.attachListeners();
        }
    }
    constructor(elementRef, renderer, zone, dragAndDrop) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.zone = zone;
        this.dragAndDrop = dragAndDrop;
        this.rcpDragStart = new EventEmitter();
        this.rcpDragEnter = new EventEmitter();
        this.rcpDragLeave = new EventEmitter();
        this.rcpDrop = new EventEmitter();
        this.count = 0;
        this.activated = false;
    }
    ngOnInit() {
        this.attachListeners();
    }
    ngOnDestroy() {
        this.detachListeners();
    }
    attachListeners() {
        const element = this.elementRef.nativeElement;
        this.zone.runOutsideAngular(() => {
            if (!this.dragOverListener) {
                this.dragOverListener = this.renderer.listen(element, 'dragover', this.onDragOver.bind(this));
            }
            if (!this.dragEnterListener) {
                this.dragEnterListener = this.renderer.listen(element, 'dragenter', this.onDragEnter.bind(this));
            }
            if (!this.dragLeaveListener) {
                this.dragLeaveListener = this.renderer.listen(element, 'dragleave', this.onDragLeave.bind(this));
            }
            if (!this.dropListener) {
                this.dropListener = this.renderer.listen(element, 'drop', this.onDrop.bind(this));
            }
        });
    }
    detachListeners() {
        this.zone.runOutsideAngular(() => {
            if (this.dragOverListener) {
                this.dragOverListener();
            }
            if (this.dragEnterListener) {
                this.dragEnterListener();
            }
            if (this.dragLeaveListener) {
                this.dragLeaveListener();
            }
            if (this.dropListener) {
                this.dropListener();
            }
        });
    }
    onDragEnter(event) {
        if (this.count === 0) {
            const helperEvent = this.makeDragHelperEvent(event, 'enter');
            this.activated = this.rcpDroppable(helperEvent);
            if (this.activated) {
                this.zone.run(() => {
                    this.renderer.addClass(this.elementRef.nativeElement, this.rcpDropOverClass);
                    this.rcpDragEnter.emit(helperEvent);
                });
            }
        }
        this.count++;
        event.stopPropagation();
    }
    onDragOver(event) {
        if (this.activated) {
            event.preventDefault();
        }
        event.stopPropagation();
    }
    onDragLeave(event) {
        this.count--;
        if (this.count === 0 && this.activated) {
            const helperEvent = this.makeDragHelperEvent(event, 'leave');
            this.zone.run(() => {
                this.renderer.removeClass(this.elementRef.nativeElement, this.rcpDropOverClass);
                this.rcpDragLeave.emit(helperEvent);
            });
            this.activated = false;
        }
        event.stopPropagation();
    }
    onDrop(event) {
        this.count = 0;
        if (this.activated) {
            const helperEvent = this.makeDragHelperEvent(event, 'drop');
            this.zone.run(() => {
                this.renderer.removeClass(this.elementRef.nativeElement, this.rcpDropOverClass);
                this.rcpDrop.emit(helperEvent);
            });
            this.activated = false;
            event.preventDefault();
        }
        event.stopPropagation();
    }
    makeDragHelperEvent(event, name) {
        if (this.hasFiles(event)) {
            return {
                name: name,
                type: 'files',
                data: event.dataTransfer
            };
        }
        return {
            name: name,
            type: this.dragAndDrop.type,
            data: this.dragAndDrop.data
        };
    }
    hasFiles(event) {
        if (!event.dataTransfer) {
            return false;
        }
        const types = event.dataTransfer.types;
        if (types instanceof DOMStringList) {
            return types.contains('Files');
        }
        else if (types instanceof Array) {
            return types.includes('Files');
        }
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: DroppableDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.NgZone }, { token: DragAndDropService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: DroppableDirective, selector: "[rcpDroppable]", inputs: { rcpDroppable: "rcpDroppable", disabled: "disabled", rcpDropOverClass: "rcpDropOverClass" }, outputs: { rcpDragStart: "rcpDragStart", rcpDragEnter: "rcpDragEnter", rcpDragLeave: "rcpDragLeave", rcpDrop: "rcpDrop" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: DroppableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[rcpDroppable]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.NgZone }, { type: DragAndDropService }], propDecorators: { rcpDroppable: [{
                type: Input
            }], disabled: [{
                type: Input
            }], rcpDropOverClass: [{
                type: Input
            }], rcpDragStart: [{
                type: Output
            }], rcpDragEnter: [{
                type: Output
            }], rcpDragLeave: [{
                type: Output
            }], rcpDrop: [{
                type: Output
            }] } });

class ReadCubeDragAndDropModule {
    static forRoot() {
        return {
            ngModule: ReadCubeDragAndDropModule,
            providers: [
                DragAndDropService
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeDragAndDropModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeDragAndDropModule, declarations: [DraggableDirective,
            DroppableDirective], imports: [CommonModule], exports: [DraggableDirective,
            DroppableDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeDragAndDropModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: ReadCubeDragAndDropModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    declarations: [
                        DraggableDirective,
                        DroppableDirective
                    ],
                    exports: [
                        DraggableDirective,
                        DroppableDirective
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DragAndDropService, DraggableDirective, DroppableDirective, ReadCubeDragAndDropModule };
